import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from 'react-router-dom';
import { apiUrl } from 'config/api';

export default function Start() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const toValidUserId = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  };

  // Try to resolve userId from navigation state or storage
  const userId = useMemo(() => {
    const fromState = location?.state?.userId || location?.state?.user_id;
    const fromStateId = toValidUserId(fromState);
    if (fromStateId) return fromStateId;
    const ls = typeof window !== 'undefined' ? window.localStorage : null;
    const ss = typeof window !== 'undefined' ? window.sessionStorage : null;
    const idCandidates = [
      ss?.getItem('userId'), ss?.getItem('user_id'),
      ls?.getItem('userId'), ls?.getItem('user_id')
    ].filter(Boolean);
    return idCandidates.length ? toValidUserId(idCandidates[0]) : undefined;
  }, [location]);

  const [percentage, setPercentage] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const timerRef = useRef(null);

  // Poll init status until 100%
  useEffect(() => {
    if (!userId) return;

    let stopped = false;

    const fetchStatus = async () => {
      try {
        const res = await fetch(apiUrl(`/api/users/${userId}/init_status`));
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const pct = Math.max(0, Math.min(100, Number(data?.percentage || 0)));
        const tl  = Math.max(0, Number(data?.time_left_seconds || 0));
        setPercentage(prev => Math.max(prev, pct)); // monotonic on the client too
        setTimeLeft(tl);
        if (pct >= 100 && timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          stopped = true;
          setTimeout(() => navigate('/news'), 1000);
        }
      } catch (e) {
        // keep silent but stop spamming if endpoint fails repeatedly
        console.warn('init_status fetch failed', e);
      }
    };

    // Kick once immediately, then poll every second
    fetchStatus();
    if (!timerRef.current) {
      timerRef.current = setInterval(fetchStatus, 1000);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [userId, navigate]);

  useEffect(() => {
    if (userId) return;
    const timeout = setTimeout(() => navigate('/login'), 1200);
    return () => clearTimeout(timeout);
  }, [userId, navigate]);

  const pageBgStyle = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 24px',
    background:
      'radial-gradient(1200px 600px at 20% 60%, rgba(255,86,120,0.18), transparent 60%), ' +
      'radial-gradient(1200px 600px at 90% 20%, rgba(61,132,255,0.18), transparent 60%), #0d1220',
  };

  const cardStyle = {
    width: '100%',
    maxWidth: 760,
    borderRadius: 24,
    padding: 28,
    background:
      'linear-gradient(0deg, black 0%, black 100%), rgba(51, 51, 51, 0.79), rgba(153, 153, 153, 0.30)',
    backgroundBlendMode: 'linear_dodge, luminosity, darken',
    boxShadow: '0px 0px 24px rgba(242,242,242,0.16) inset, 0 10px 32px rgba(0,0,0,0.35)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    color: '#F5F7FA',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  };

  const glareStyle = {
    position: 'absolute',
    left: -60,
    top: -60,
    width: '60%',
    height: '60%',
    background: 'radial-gradient(closest-side, rgba(255,255,255,0.18), transparent 65%)',
    filter: 'blur(10px)',
    pointerEvents: 'none',
  };

  const headlineStyle = {
    fontSize: 28,
    lineHeight: '34px',
    fontWeight: 700,
    margin: 0,
    letterSpacing: 0.2,
    textAlign: 'center',
    textTransform: 'uppercase',
    color: 'white',
    fontFamily: "'Neo Sans', sans-serif",
  };

  const subStyle = {
    marginTop: 12,
    fontSize: 16,
    lineHeight: '24px',
    color: 'rgba(235,240,255,0.85)',
    fontFamily: "'Neo Sans', sans-serif",
  };

  const progressLabelStyle = {
    color: 'rgba(255,255,255,0.9)',
    fontSize: 12,
    letterSpacing: 0.2,
    fontFamily: "'Neo Sans', sans-serif",
  };

  const progressRemainingStyle = {
    color: 'rgba(255,255,255,0.85)',
    fontSize: 12,
    fontFamily: "'Neo Sans', sans-serif",
  };

  const badgeStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 10px',
    borderRadius: 999,
    fontSize: 12,
    letterSpacing: 0.4,
    color: '#E9EEF7',
    background:
      'linear-gradient(0deg, black 0%, black 100%), rgba(80, 80, 80, 0.65), rgba(180, 180, 180, 0.28)',
    backgroundBlendMode: 'linear_dodge, luminosity, darken',
    boxShadow: '0px 0px 14px rgba(242,242,242,0.18) inset',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
  };

  const fmtTime = (secs) => {
    const s = Math.max(0, Math.floor(secs || 0));
    const m = Math.floor(s / 60);
    const r = s % 60;
    const pad = (n) => (n < 10 ? `0${n}` : String(n));
    return `${m}:${pad(r)}`;
  };

  return (
    <div style={pageBgStyle}>
      <div style={cardStyle}>
        <div style={glareStyle} />

        <h1 style={headlineStyle}>{t("onboarding.start.preparingDemo")}</h1>

        {/* Lottie animation placeholder 
        <div style={{ height: 150, marginTop: 20, marginBottom: 20, backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: 12 }} />
        */}

        <p style={{ ...subStyle, textAlign: 'center' }}>
          {t("onboarding.start.preparingDemoSubtitle")}
        </p>

        {/* Progress Row */}
        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
            <div style={progressLabelStyle}>{t("onboarding.start.progress")}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={progressLabelStyle}>{Math.max(0, Math.min(100, Math.round(percentage)))}%</div>
              <div style={progressRemainingStyle}>{t("onboarding.start.remaining", { value: fmtTime(timeLeft) })}</div>
            </div>
          </div>
          <div style={{ height: 10, borderRadius: 8, background: 'rgba(255,255,255,0.15)', overflow: 'hidden', boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.12)' }}>
            <div style={{ width: `${Math.max(0, Math.min(100, Math.round(percentage)))}%`, height: '100%', background: 'linear-gradient(90deg, rgba(255,255,255,0.9), rgba(255,255,255,0.45))', transition: 'width 240ms ease', borderRadius: 8 }} />
          </div>
        </div>
      </div>
    </div>
  );
}
